import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/features/auth/AuthProvider";
import { queryKeys } from "@/shared/lib/queryKeys";
import { logger } from "@/shared/lib/logger";

interface AccountStatus {
  account_status: string | null;
  strike_count: number;
  isSuspended: boolean;
}

/**
 * Lazy account-status check (suspension + strikes). Runs only after auth
 * has resolved a user. Cached so guards / banners / settings all share one
 * fetch.
 *
 * Defense-in-depth: if the profile row doesn't exist yet (brand-new user
 * mid-signup), we treat the account as active — identity is already
 * verified by `useAuth()`.
 */
export const useAccountStatus = () => {
  const { user, loading: authLoading } = useAuth();

  const query = useQuery({
    queryKey: queryKeys.auth.accountStatus(user?.id),
    enabled: !!user?.id && !authLoading,
    staleTime: 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<AccountStatus> => {
      const { data, error } = await supabase
        .from("profiles_safe")
        .select("account_status, strike_count")
        .eq("id", user!.id)
        .maybeSingle();

      if (error) {
        logger.error("[useAccountStatus] profile fetch error:", error);
        throw error;
      }

      const status = data?.account_status ?? null;
      const strikes = data?.strike_count ?? 0;
      let suspended = status === "suspended";

      // 3+ strikes triggers a server-side suspension check that may flip
      // the account_status. Run it here so guards see the latest value.
      if (!suspended && strikes >= 3) {
        try {
          const { data: stillActive } = await supabase.rpc(
            "check_user_suspension",
            { check_user_id: user!.id }
          );
          if (stillActive === false) suspended = true;
        } catch (e) {
          logger.warn("[useAccountStatus] suspension RPC failed:", e);
        }
      }

      return {
        account_status: status,
        strike_count: strikes,
        isSuspended: suspended,
      };
    },
  });

  return {
    accountStatus: query.data?.account_status ?? null,
    strikeCount: query.data?.strike_count ?? 0,
    isSuspended: query.data?.isSuspended ?? false,
    loading: authLoading || (!!user?.id && query.isPending),
    error: query.error,
  };
};
